import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, QueryList, ViewChildren } from '@angular/core';
import {
  CdkDragDrop,
  CdkDragMove,
  CdkDropList,
} from '@angular/cdk/drag-drop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { ModalController } from '@ionic/angular';

import { AreaProperty } from '@models/area-property.model';
import { AreaPropertyOption } from '@models/area-property-option.model';
import { InputType, InputTypeLabels } from '@models/input-type.enum';
import { AreaGeoPolygon, AreaGeoValue } from '@models/area-geo-value.model';
import { AreaPropertyTreeService } from '../../services/area-property-tree.service';
import { DeleteModalComponent } from '@shared/components/delete-modal/delete-modal.component';

@Component({
  standalone: false,
  selector: 'app-area-structure-view',
  templateUrl: './area-structure-view.component.html',
  styleUrls: ['./area-structure-view.component.scss'],
})
export class AreaStructureViewComponent implements OnChanges, AfterViewInit {
  @Input() areaId!: string;
  @Input() properties: AreaProperty[] = [];
  @Input() showSaveButton = false;
  @Input() saveButtonLabel = 'Guardar';
  @Input() title = 'Estructura de las propiedades';
  /**
   * Si se indica, solo las propiedades con ese source son editables/eliminables.
   * Las demás se muestran en modo lectura.
   * null = todas editables (comportamiento admin por defecto).
   */
  @Input() editableSource: 'template' | 'custom' | null = null;
  /** Si true, las propiedades con isLocked=true no se pueden editar ni borrar (pero sí mover) */
  @Input() lockEnabled = false;
  /** Propiedades del usuario: el Polígono solo lo define el admin en la plantilla (el servidor tampoco deja crearlo). */
  @Input() allowPolygonType = true;
  newOptionText: string = '';

  /** El elemento borrado, mas todos sus descendientes (secciones/campos anidados) que se van con el. */
  @Output() propertyDeleted = new EventEmitter<AreaProperty[]>();
  @Output() saveClicked = new EventEmitter<void>();
  @Output() propertyLockToggled = new EventEmitter<AreaProperty>();

  @ViewChildren('nameInput') nameInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren(CdkDropList) dropLists!: QueryList<CdkDropList<AreaProperty[]>>;

  areaPropertyByHierarchy: AreaProperty[] = [];

  editingPropertyId: string | null = null;
  activeSectionId: string | null = null;
  hoverSectionId: string | null = null;
  hoverListId: string | null = null;
  private lastPointerY = 0;
  editForm!: FormGroup;

  private readonly allPropertyTypes = Object.values(InputType);

  /** Un Polígono que ya existia sigue saliendo: si no, el selector se quedaria sin su tipo. */
  get propertyTypes(): InputType[] {
    if (this.allowPolygonType || this.editForm?.get('inputType')?.value === InputType.GEOPOLYGON) return this.allPropertyTypes;
    return this.allPropertyTypes.filter(type => type !== InputType.GEOPOLYGON);
  }
  inputTypeLabels = InputTypeLabels;

  connectedDropLists: CdkDropList<AreaProperty[]>[] = [];

  /** ids de propiedades nuevas que aún no se han guardado */
  private newlyCreatedIds = new Set<string>();
  private collapsedNodeIds = new Set<string>();
  private initializedNodeCollapseIds = new Set<string>();
  private readonly autoCollapseThreshold = 6;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private treeService: AreaPropertyTreeService,
    private modalController: ModalController,
  ) {
    this.initForm();
  }

  /**
   * "Ubicación" y "Polígono" guardan un objeto (no un texto/número), así que
   * la interpolación directa de prop.value mostraría "[object Object]". Aquí
   * se traduce cada tipo a un resumen legible para el badge "Valor:".
   */
  getValueSummary(prop: AreaProperty): string {
    const value = prop.value;
    if (value === null || value === undefined || value === '') return '';

    if (prop.inputType === InputType.LOCATION) {
      const point = value as AreaGeoValue;
      return typeof point?.lat === 'number' && typeof point?.lng === 'number'
        ? `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`
        : '';
    }

    if (prop.inputType === InputType.GEOPOLYGON) {
      const polygon = value as AreaGeoPolygon;
      return typeof polygon?.areaHa === 'number' ? `${polygon.areaHa.toFixed(2)} Ha` : 'Polígono definido';
    }

    return String(value);
  }

  openPreview(): void {
    localStorage.setItem('area_structure_preview', JSON.stringify(this.properties));
    window.open('#/superadmin/areas/preview', '_blank');
  }

  onSaveClick(): void {
    this.confirmPendingEdit();
    localStorage.setItem('area_structure_preview', JSON.stringify(this.properties));
    this.saveClicked.emit();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideCdkOverlay = !!target.closest('.cdk-overlay-container');

    // Cerrar formulario de edición al hacer click fuera
    if (this.editingPropertyId) {
      const editingNode = this.el.nativeElement.querySelector('.node.editing');
      if (editingNode && !editingNode.contains(target) && !isInsideCdkOverlay) {
        this.confirmPendingEdit();
      }
    }

  }

  private initForm() {
    this.editForm = this.fb.group({
      label: [''],
      inputType: [InputType.TEXT],
      unit: [''],
      isRequired: [false],
      value: [null],
      parentId: [null],
      options: [[]],
      // 🔮 A FUTURO: descomentar cuando se reactive el toggle "Activar secciones por opción"
      // hasSectionPerOption: [true],
    });
  }

  // ────────────────────────────────────────────────
  // Ciclo de vida
  // ────────────────────────────────────────────────

  ngOnChanges(): void {
    this.editingPropertyId = null;
    this.newlyCreatedIds.clear();
    this.newOptionText = '';
    this.areaPropertyByHierarchy = this.treeService.buildTree(this.properties);
    this.syncNodeCollapseState();
  }

  ngAfterViewInit(): void {
    // 1ª vez: inicializar listas conectadas y forzar un ciclo de detección
    this.updateConnectedDropLists();
    this.cdr.detectChanges();

    // Cuando cambie la QueryList (nuevas secciones/hijos),
    // volvemos a actualizar y forzamos otro detectChanges.
    this.dropLists.changes.subscribe(() => {
      this.updateConnectedDropLists();
      this.cdr.detectChanges();
    });
  }

  private updateConnectedDropLists() {
    this.connectedDropLists = this.dropLists.toArray();
  }

  get isEmpty(): boolean {
    return !this.areaPropertyByHierarchy || this.areaPropertyByHierarchy.length === 0;
  }

  /** Devuelve true si esta propiedad es solo lectura por source (controla drag) */
  isReadOnly(prop: AreaProperty): boolean {
    if (this.editableSource === null) return false;
    return prop.source !== this.editableSource;
  }

  /** Devuelve true si no se puede editar ni borrar (isLocked en contexto usuario, o readonly por source) */
  isEditDisabled(prop: AreaProperty): boolean {
    if (this.lockEnabled && prop.isLocked) return true;
    return this.isReadOnly(prop);
  }

  // ────────────────────────────────────────────────
  // Crear propiedades / secciones raíz
  // ────────────────────────────────────────────────

  startCreateSection(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.createAndEditProperty(InputType.SECTION);
  }

  startCreateProperty(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.createAndEditProperty(InputType.TEXT);
  }

  private createAndEditProperty(type: InputType): void {
    // Si hay una edición en curso, confirmarla antes de crear una nueva
    if (this.editingPropertyId) {
      this.confirmPendingEdit();
    }

    const newProp: AreaProperty = {
      id: uuidv4(),
      areaId: this.areaId,
      parentId: this.activeSectionId ?? null,
      keyName: '',
      label: '',
      inputType: type,
      unit: '',
      isRequired: false,
      orderIndex: 0,
      value: null,
      options: [],
      children: [],
      source: this.editableSource ?? undefined,
    } as AreaProperty;

    // se añade al árbol pero NO se sincroniza al plano hasta guardar
    if (this.activeSectionId) {
      const parentFound = this.treeService.locateNodeInTree(this.activeSectionId, this.areaPropertyByHierarchy);
      if (parentFound) {
        parentFound.node.children ??= [];
        parentFound.node.children.push(newProp);
      } else {
        this.areaPropertyByHierarchy.push(newProp);
      }
    } else {
      this.areaPropertyByHierarchy.push(newProp);
    }
    this.newlyCreatedIds.add(newProp.id);

    this.startEdit(newProp);
  }

  private focusLastNameInput(): void {
    if (!this.nameInputs || this.nameInputs.length === 0) return;
    const inputRef = this.nameInputs.last;
    if (inputRef && inputRef.nativeElement) {
      inputRef.nativeElement.focus();
      inputRef.nativeElement.select();
    }
  }

  // ────────────────────────────────────────────────
  // Edición en línea
  // ────────────────────────────────────────────────

  setActiveSection(sectionId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.activeSectionId = this.activeSectionId === sectionId ? null : sectionId;
  }

  toggleNodeCollapse(node: AreaProperty, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (!node.id || !this.isCollapsibleNode(node)) return;

    if (this.collapsedNodeIds.has(node.id)) {
      this.collapsedNodeIds.delete(node.id);
      return;
    }

    this.collapsedNodeIds.add(node.id);
  }

  isNodeCollapsed(node: AreaProperty): boolean {
    if (!node.id || !this.isCollapsibleNode(node)) return false;
    if (this.nodeContainsId(node, this.editingPropertyId)) return false;
    return this.collapsedNodeIds.has(node.id);
  }

  getNodeContentCount(node: AreaProperty): number {
    return this.countDescendants(node.children ?? []);
  }

  getCollapseTooltip(node: AreaProperty): string {
    const noun = node.inputType === InputType.SECTION ? 'sección' : 'contenido';
    return this.isNodeCollapsed(node) ? `Expandir ${noun}` : `Minimizar ${noun}`;
  }

  clearActiveSection(event: MouseEvent): void {
    event.stopPropagation();
    this.activeSectionId = null;
  }

  startEdit(prop: AreaProperty, event?: MouseEvent): void {
    if (event) event.stopPropagation();

    if (prop.inputType === InputType.SECTION) {
      this.activeSectionId = prop.id;
    }

    this.editingPropertyId = prop.id;

    // 👇 Patch síncrono, dentro del mismo ciclo que el click
    this.editForm.patchValue({
      label: prop.label || '',
      inputType: prop.inputType as InputType,
      unit: prop.unit || '',
      isRequired: !!prop.isRequired,
      value: prop.value ?? null,
      parentId: prop.parentId ?? null,
      options: prop.options ? [...prop.options] : [],
      // 🔮 A FUTURO: descomentar cuando se reactive el toggle "Activar secciones por opción"
      // hasSectionPerOption: prop.hasSectionPerOption ?? true,
    });

    this.newOptionText = '';

    // Solo el foco va en siguiente tick (esto no afecta al error)
    setTimeout(() => this.focusLastNameInput(), 0);
  }

  /** ¿Hay una edición abierta con el nombre vacío? Llamarlo ANTES de confirmPendingEdit(),
   *  que si no la borraría en silencio en vez de avisar. */
  hasPendingUnnamedEdit(): boolean {
    if (!this.editingPropertyId) return false;
    const label = this.editForm.get('label')?.value;
    return !label || !String(label).trim();
  }

  /** Llamado desde el componente padre antes de persistir al backend */
  confirmPendingEdit(): void {
    if (!this.editingPropertyId) return;
    const found = this.treeService.locateNodeInTree(this.editingPropertyId, this.areaPropertyByHierarchy);
    if (!found) return;
    this.ensureGeoDefaultLabel();
    const prop = found.node;
    const label = this.editForm.get('label')?.value?.trim();
    if (label) {
      this.saveEdit(prop);
    } else {
      this.cancelEdit();
    }
  }

  cancelEdit(event?: MouseEvent): void {
    if (event) event.stopPropagation();

    const id = this.editingPropertyId;

    // si era nueva y no se ha guardado -> eliminarla del árbol
    if (id && this.newlyCreatedIds.has(id)) {
      this.treeService.removeNodeById(id, this.areaPropertyByHierarchy);
      this.newlyCreatedIds.delete(id);
      this.updateFlatPropertiesFromTree();
    }

    this.editingPropertyId = null;
    this.newOptionText = '';
  }

  saveEdit(prop: AreaProperty, event?: MouseEvent): void {
    if (event) event.stopPropagation();

    const { label, inputType, unit, isRequired, value, parentId, options } =
      this.editForm.value;

    const found = this.treeService.locateNodeInTree(prop.id, this.areaPropertyByHierarchy);
    const oldParentId = found?.parent ? found.parent.id : null;

    prop.label = label;
    prop.inputType = inputType;
    prop.unit = unit;
    prop.isRequired = isRequired;
    prop.value = value;
    // 🔮 A FUTURO: cuando se reactive el toggle, sustituir esta línea por:
    // prop.hasSectionPerOption = this.editForm.value.hasSectionPerOption ?? false;
    prop.hasSectionPerOption = inputType === InputType.LIST;
    prop.options = inputType === InputType.LIST ? (options || []) : prop.options;

    if (!prop.keyName || prop.keyName.trim() === '') {
      prop.keyName = this.treeService.generateKeyName(label);
    }

    // Si ha cambiado de sección (parentId) → reubicar en el árbol
    if (found && parentId !== oldParentId) {
      const { node, list } = found;
      const idx = list.indexOf(node);
      if (idx > -1) list.splice(idx, 1);

      if (parentId) {
        const newParentFound = this.treeService.locateNodeInTree(parentId, this.areaPropertyByHierarchy);
        if (newParentFound) {
          const parentNode = newParentFound.node;
          parentNode.children ??= [];
          parentNode.children.push(node);
        } else {
          this.areaPropertyByHierarchy.push(node);
        }
      } else {
        // sin sección → raíz
        this.areaPropertyByHierarchy.push(node);
      }
    }

    this.newlyCreatedIds.delete(prop.id);
    this.updateFlatPropertiesFromTree();
    this.editingPropertyId = null;
    this.newOptionText = '';
  }

  /**
   * El toggle "Obligatorio" no tiene un gesto de confirmación explícito (como Enter en un input),
   * así que no puede pasar por confirmPendingEdit(): si el Nombre aún está vacío, esa función
   * interpreta que se ha abandonado la edición y borra la propiedad recién creada del árbol.
   * Aquí solo se actualiza el dato en el nodo, sin cerrar la edición ni poder eliminar nada.
   */
  onRequiredToggleChange(): void {
    if (!this.editingPropertyId) return;
    const found = this.treeService.locateNodeInTree(this.editingPropertyId, this.areaPropertyByHierarchy);
    if (!found) return;
    found.node.isRequired = this.editForm.get('isRequired')?.value;
  }

  onInputTypeChange(): void {
    const type = this.editForm.get('inputType')?.value as InputType;
    this.ensureGeoDefaultLabel(type);

    if (type === InputType.BOOLEAN) {
      this.editForm.patchValue({ value: false, unit: '' });
    } else if (type === InputType.NUMBER) {
      this.editForm.patchValue({ value: null });
    } else if (type === InputType.DATE) {
      this.editForm.patchValue({ value: null, unit: '' });
    } else if (type === InputType.SECTION) {
      this.editForm.patchValue({ value: null, unit: '', options: [] });
    } else if (type === InputType.LIST) {
      this.editForm.patchValue({ value: null, unit: '' });
      this.editForm.get('options')?.setValue(this.editForm.get('options')?.value || []);
    } else if (type === InputType.LOCATION || type === InputType.GEOPOLYGON) {
      this.editForm.patchValue({ value: null, unit: '' });
    } else {
      this.editForm.patchValue({ value: '' });
    }
  }

  private ensureGeoDefaultLabel(type = this.editForm.get('inputType')?.value as InputType): void {
    const defaultLabel = this.getGeoDefaultLabel(type);
    if (!defaultLabel) return;

    const labelControl = this.editForm.get('label');
    const currentLabel = (labelControl?.value || '').trim();

    if (!currentLabel || this.isGeoDefaultLabel(currentLabel)) {
      labelControl?.setValue(defaultLabel);
    }
  }

  private getGeoDefaultLabel(type: InputType): string | null {
    if (type === InputType.LOCATION || type === InputType.GEOPOLYGON) {
      return InputTypeLabels[type];
    }

    return null;
  }

  private isGeoDefaultLabel(label: string): boolean {
    return [InputType.LOCATION, InputType.GEOPOLYGON]
      .map((type: InputType) => InputTypeLabels[type])
      .includes(label);
  }

  // ────────────────────────────────────────────────
  // Opciones de LIST
  // ────────────────────────────────────────────────

  addListOption(): void {
    const value = (this.newOptionText || '').trim();
    if (!value) return;

    const opts = (this.editForm.get('options')?.value || []) as AreaPropertyOption[];
    const newOption: AreaPropertyOption = { label: value, value };

    const section = this.createLinkedSection(value);
    newOption.sectionAreaPropertyId = section.id;

    opts.push(newOption);
    this.editForm.patchValue({ options: opts });
    this.newOptionText = '';
  }

  removeListOption(index: number): void {
    const opts = (this.editForm.get('options')?.value || []) as AreaPropertyOption[];
    if (index < 0 || index >= opts.length) return;

    const removedOpt = opts[index];
    if (removedOpt.sectionAreaPropertyId) {
      this.treeService.removeNodeById(removedOpt.sectionAreaPropertyId, this.areaPropertyByHierarchy);
      this.updateFlatPropertiesFromTree();
    }

    opts.splice(index, 1);
    this.editForm.patchValue({ options: opts });

    // si el valor por defecto ya no existe, lo limpiamos
    const currentValue = this.editForm.get('value')?.value;
    const stillExists = opts.some(o => o.value === currentValue);
    if (!stillExists) {
      this.editForm.patchValue({ value: null });
    }
  }

  // ────────────────────────────────────────────────
  // Secciones vinculadas a opciones de lista
  // ────────────────────────────────────────────────

  addSectionToOption(listProp: AreaProperty, opt: AreaPropertyOption, event: MouseEvent): void {
    event.stopPropagation();
    const section = this.createLinkedSection(opt.value, listProp.id);
    const opts = (listProp.options ?? []).map(o =>
      o.value === opt.value ? { ...o, sectionAreaPropertyId: section.id } : o
    );
    listProp.options = opts;
    this.updateFlatPropertiesFromTree();
  }

  /** Garantiza que la lista tenga keyName y lo devuelve */
  private ensureListKeyName(propId?: string): string {
    const id = propId ?? this.editingPropertyId;
    if (!id) return uuidv4();
    const found = this.treeService.locateNodeInTree(id, this.areaPropertyByHierarchy);
    if (!found) return uuidv4();
    const prop = found.node;
    if (!prop.keyName || prop.keyName.trim() === '') {
      const label = this.editForm.get('label')?.value || prop.label || 'lista';
      prop.keyName = this.treeService.generateKeyName(label);
    }
    return prop.keyName;
  }

  /** Crea una sección vinculada a una opción y la añade como hijo de la lista */
  private createLinkedSection(optionValue: string, listPropId?: string): AreaProperty {
    const listKeyName = this.ensureListKeyName(listPropId);
    const refId = listPropId ?? this.editingPropertyId;
    const safeValue = optionValue.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^\w_]/g, '');
    const section: AreaProperty = {
      id: uuidv4(),
      areaId: this.areaId,
      parentId: refId ?? null,
      keyName: `section_${listKeyName}_${safeValue}`,
      label: optionValue,
      inputType: InputType.SECTION,
      isRequired: false,
      orderIndex: 0,
      value: null,
      options: [],
      children: [],
      source: this.editableSource ?? undefined,
      hasSectionPerOption: false,
      visibilityCondition: {
        operator: 'AND',
        rules: [{ field: listKeyName, op: 'eq', value: optionValue }],
      },
    } as AreaProperty;

    if (refId) {
      const found = this.treeService.locateNodeInTree(refId, this.areaPropertyByHierarchy);
      if (found) {
        if (!found.node.children) found.node.children = [];
        found.node.children.push(section);
      } else {
        this.areaPropertyByHierarchy.push(section);
      }
    } else {
      this.areaPropertyByHierarchy.push(section);
    }

    this.updateFlatPropertiesFromTree();
    return section;
  }

  getAvailableSections(current: AreaProperty): AreaProperty[] {
    const sections = this.properties.filter(p => p.inputType === InputType.SECTION);
    const found = this.treeService.locateNodeInTree(current.id, this.areaPropertyByHierarchy);
    if (!found) {
      return sections.filter(s => s.id !== current.id);
    }

    const disallowed = new Set<string>();
    disallowed.add(current.id);
    this.treeService.collectDescendantIds(found.node, disallowed);

    return sections.filter(s => !disallowed.has(s.id));
  }

  // ────────────────────────────────────────────────
  // Eliminar + drag & drop
  // ────────────────────────────────────────────────

  moveUp(prop: AreaProperty, event?: MouseEvent): void {
    if (event) event.stopPropagation();

    const found = this.treeService.locateNodeInTree(prop.id, this.areaPropertyByHierarchy);
    if (!found) return;

    const { node, parent, list } = found;
    const idx = list.indexOf(node);

    if (idx > 0) {
      const prevSibling = list[idx - 1];
      if (prevSibling.inputType === InputType.SECTION) {
        // Entra dentro de la sección anterior como último elemento
        list.splice(idx, 1);
        if (!prevSibling.children) prevSibling.children = [];
        prevSibling.children.push(node);
        node.parentId = prevSibling.id;
      } else {
        [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
      }
    } else if (idx === 0 && parent !== null) {
      // Sale de la sección: se coloca antes del padre en el nivel superior
      list.splice(idx, 1);
      const parentFound = this.treeService.locateNodeInTree(parent.id, this.areaPropertyByHierarchy);
      const parentList = parentFound ? parentFound.list : this.areaPropertyByHierarchy;
      const parentIdx = parentList.indexOf(parent);
      parentList.splice(parentIdx, 0, node);
      node.parentId = parentFound?.parent?.id ?? null;
    }

    this.updateFlatPropertiesFromTree();
    this.syncEditingParentControl(node);
  }

  moveDown(prop: AreaProperty, event?: MouseEvent): void {
    if (event) event.stopPropagation();

    const found = this.treeService.locateNodeInTree(prop.id, this.areaPropertyByHierarchy);
    if (!found) return;

    const { node, parent, list } = found;
    const idx = list.indexOf(node);

    if (idx < list.length - 1) {
      const nextSibling = list[idx + 1];
      if (nextSibling.inputType === InputType.SECTION) {
        // Entra dentro de la sección siguiente como primer elemento
        list.splice(idx, 1);
        if (!nextSibling.children) nextSibling.children = [];
        nextSibling.children.unshift(node);
        node.parentId = nextSibling.id;
      } else {
        [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];
      }
    } else if (idx === list.length - 1 && parent !== null) {
      // Sale de la sección: se coloca después del padre en el nivel superior
      list.splice(idx, 1);
      const parentFound = this.treeService.locateNodeInTree(parent.id, this.areaPropertyByHierarchy);
      const parentList = parentFound ? parentFound.list : this.areaPropertyByHierarchy;
      const parentIdx = parentList.indexOf(parent);
      parentList.splice(parentIdx + 1, 0, node);
      node.parentId = parentFound?.parent?.id ?? null;
    }

    this.updateFlatPropertiesFromTree();
    this.syncEditingParentControl(node);
  }

  toggleLock(prop: AreaProperty, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    const nextLocked = !prop.isLocked;
    prop.isLocked = nextLocked;

    const found = this.treeService.locateNodeInTree(prop.id, this.areaPropertyByHierarchy);
    this.applyLockToDescendants(found?.node.children ?? [], nextLocked);

    this.updateFlatPropertiesFromTree();
    this.propertyLockToggled.emit(prop);
  }

  /** Bloquear/desbloquear un nodo arrastra el mismo estado a todo lo que cuelga de él (listas, secciones). */
  private applyLockToDescendants(children: AreaProperty[], locked: boolean): void {
    for (const child of children) {
      child.isLocked = locked;
      this.applyLockToDescendants(child.children ?? [], locked);
    }
  }

  /**
   * Si se elimina (o se saca de su lista) una sección vinculada a una opción
   * de lista, elimina también esa opción y limpia el valor por defecto de la
   * lista si quedó inválido. Usado al borrar y al mover (`confirmDetachIfLinked`).
   */
  private removeLinkedListOptionsForSection(section: AreaProperty): void {
    const linkedSectionIds = new Set<string>([section.id]);
    this.treeService.collectDescendantIds(section, linkedSectionIds);

    const removeLinkedOptions = (options: AreaPropertyOption[]): AreaPropertyOption[] =>
      options.filter((option: AreaPropertyOption) =>
        !option.sectionAreaPropertyId || !linkedSectionIds.has(option.sectionAreaPropertyId)
      );

    const removedValuesFrom = (options: AreaPropertyOption[]): Set<string> =>
      new Set(
        options
          .filter((option: AreaPropertyOption) =>
            !!option.sectionAreaPropertyId && linkedSectionIds.has(option.sectionAreaPropertyId)
          )
          .map((option: AreaPropertyOption) => option.value)
      );

    const removeInTree = (list: AreaProperty[]) => {
      for (const node of list) {
        const options = node.options ?? [];
        const removedValues = removedValuesFrom(options);

        if (removedValues.size > 0) {
          node.options = removeLinkedOptions(options);
          if (removedValues.has(node.value)) {
            node.value = null;
          }
        }

        if (node.children?.length) {
          removeInTree(node.children);
        }
      }
    };

    removeInTree(this.areaPropertyByHierarchy);

    if (this.editingPropertyId && this.editForm) {
      const formOptions = (this.editForm.get('options')?.value || []) as AreaPropertyOption[];
      const removedValues = removedValuesFrom(formOptions);

      if (removedValues.size > 0) {
        const currentValue = this.editForm.get('value')?.value;
        this.editForm.patchValue({
          options: removeLinkedOptions(formOptions),
          value: removedValues.has(currentValue) ? null : currentValue,
        });
      }
    }
  }

  deleteProperty(prop: AreaProperty, event?: MouseEvent): void {
    if (event) event.stopPropagation();

    // Si la sección borrada estaba vinculada a una opción de lista, quitar también esa opción
    if (prop.inputType === InputType.SECTION) {
      this.removeLinkedListOptionsForSection(prop);
    }

    const removedSubtree = this.collectSubtree(prop);

    this.treeService.removeNodeById(prop.id, this.areaPropertyByHierarchy);
    this.newlyCreatedIds.delete(prop.id);
    this.updateFlatPropertiesFromTree();

    if (this.editingPropertyId === prop.id) {
      this.editingPropertyId = null;
    }
    this.newOptionText = '';

    this.propertyDeleted.emit(removedSubtree);
  }

  /** El nodo mas todos sus descendientes, sin tocar parentId/orden — para avisar de un borrado en bloque. */
  private collectSubtree(prop: AreaProperty): AreaProperty[] {
    const result: AreaProperty[] = [prop];
    const walk = (children?: AreaProperty[]) => {
      (children ?? []).forEach(child => {
        result.push(child);
        walk(child.children);
      });
    };
    walk(prop.children);
    return result;
  }

  onDragMoved(event: CdkDragMove<AreaProperty>, draggingProp: AreaProperty): void {
    const { x, y } = event.pointerPosition;
    this.lastPointerY = y;
    this.hoverSectionId = null;

    const checkElements = (selector: string) => {
      this.el.nativeElement.querySelectorAll(selector).forEach((el: Element) => {
        const sectionId = (el as HTMLElement).getAttribute('data-section-id');
        if (!sectionId || sectionId === draggingProp.id) return;
        const rect = (el as HTMLElement).getBoundingClientRect();
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          this.hoverSectionId = sectionId;
        }
      });
    };

    // Cabecera
    checkElements('.property-item.is-section > .node[data-section-id]');
    // Cuerpo (vacío o no)
    if (!this.hoverSectionId) {
      checkElements('.property-item.is-section > .children[data-section-id]');
    }

    // Una sección se puede soltar sobre la cabecera O el cuerpo de una lista
    // (entre sus opciones existentes) para convertirse en una opción nueva.
    this.hoverListId = null;
    if (draggingProp.inputType === InputType.SECTION && !this.hoverSectionId) {
      const checkListElements = (selector: string) => {
        this.el.nativeElement.querySelectorAll(selector).forEach((el: Element) => {
          const listId = (el as HTMLElement).getAttribute('data-list-id');
          if (!listId) return;
          const rect = (el as HTMLElement).getBoundingClientRect();
          if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
            this.hoverListId = listId;
          }
        });
      };

      checkListElements('.property-item.is-list > .node[data-list-id]');
      if (!this.hoverListId) {
        checkListElements('.property-item.is-list > .children[data-list-id]');
      }
    }
  }

  onDragEnded(): void {
    setTimeout(() => { this.hoverSectionId = null; this.hoverListId = null; }, 0);
  }

  async drop(event: CdkDragDrop<AreaProperty[]>): Promise<void> {
    const prevList = event.previousContainer.data;
    const movedItem = prevList[event.previousIndex];

    // Si se soltó sobre la cabecera de una lista → crear una opción nueva vinculada
    if (this.hoverListId) {
      const listFound = this.treeService.locateNodeInTree(this.hoverListId, this.areaPropertyByHierarchy);
      this.hoverListId = null;
      if (!listFound) return;
      const listProp = listFound.node;

      if (!(await this.confirmLinkAsNewOption(movedItem, listProp))) return;

      prevList.splice(event.previousIndex, 1);
      this.attachAsNewOption(movedItem, listProp);
      this.updateFlatPropertiesFromTree();
      this.syncEditingParentControl(movedItem);
      return;
    }

    // Si se soltó sobre cabecera o cuerpo de una sección → insertar dentro
    if (this.hoverSectionId) {
      const targetId = this.hoverSectionId;
      if (!(await this.confirmDetachIfLinked(movedItem, targetId))) return;
      this.hoverSectionId = null;

      prevList.splice(event.previousIndex, 1);

      const sectionFound = this.treeService.locateNodeInTree(targetId, this.areaPropertyByHierarchy);
      if (sectionFound) {
        sectionFound.node.children ??= [];

        // Calcular posición de inserción según la Y del puntero
        const childEls = this.el.nativeElement.querySelectorAll(
          `.property-item.is-section > .children[data-section-id="${targetId}"] > .property-list > .property-item`
        );
        let insertIndex = sectionFound.node.children.length;
        childEls.forEach((el: Element, i: number) => {
          const rect = (el as HTMLElement).getBoundingClientRect();
          if (this.lastPointerY < rect.top + rect.height / 2 && i < insertIndex) {
            insertIndex = i;
          }
        });

        sectionFound.node.children.splice(insertIndex, 0, movedItem);
        movedItem.parentId = targetId;
      }

      this.updateFlatPropertiesFromTree();
      this.syncEditingParentControl(movedItem);
      return;
    }

    const currList = event.container.data;

    // Evitar meter propiedades dentro de propiedades no-sección
    const newParent = this.treeService.findParentByChildrenRef(this.areaPropertyByHierarchy, currList);
    if (newParent && newParent.inputType !== 'section') return;

    if (!(await this.confirmDetachIfLinked(movedItem, newParent ? newParent.id : null))) return;

    prevList.splice(event.previousIndex, 1);
    currList.splice(event.currentIndex, 0, movedItem);

    movedItem.parentId = newParent ? newParent.id : null;

    this.updateFlatPropertiesFromTree();
    this.syncEditingParentControl(movedItem);
  }

  /**
   * Si `movedItem` es una sección vinculada a una opción de lista (icono de
   * carpeta) y el destino ya no es esa lista, avisa de que se va a perder el
   * vínculo con la opción y, si se confirma, la quita de la lista. Devuelve
   * false para cancelar el movimiento entero.
   */
  private async confirmDetachIfLinked(movedItem: AreaProperty, newParentId: string | null): Promise<boolean> {
    if (movedItem.inputType !== InputType.SECTION) return true;

    const linkedList = this.findListLinkedToSection(movedItem.id);
    if (!linkedList || linkedList.id === newParentId) return true;

    const optionLabel = (linkedList.options ?? [])
      .find(o => o.sectionAreaPropertyId === movedItem.id)?.label ?? movedItem.label;

    const modal = await this.modalController.create({
      component: DeleteModalComponent,
      componentProps: {
        modalTitle: 'Sacar sección de la lista',
        message: `Esta sección está unida a la opción "${optionLabel}" de la lista "${linkedList.label}".\n\n`
          + `Si la sacas de ahí, esa opción se eliminará de la lista.\n\n`
          + `¿Continuar?`,
        confirmLabel: 'Continuar',
        cancelColor: 'warn',
        confirmColor: 'primary',
      },
      cssClass: 'auto-height',
      backdropDismiss: false,
    });
    await modal.present();
    const { data: confirmed } = await modal.onDidDismiss();
    if (!confirmed) return false;

    this.removeLinkedListOptionsForSection(movedItem);
    return true;
  }

  /** Busca la propiedad tipo lista (en el árbol o en el formulario en edición) cuya alguna opción esté vinculada a esta sección. */
  private findListLinkedToSection(sectionId: string): AreaProperty | null {
    const isLinked = (options?: AreaPropertyOption[]) =>
      (options ?? []).some(o => o.sectionAreaPropertyId === sectionId);

    if (this.editingPropertyId) {
      const formOptions = (this.editForm.get('options')?.value || []) as AreaPropertyOption[];
      if (isLinked(formOptions)) {
        const found = this.treeService.locateNodeInTree(this.editingPropertyId, this.areaPropertyByHierarchy);
        if (found) return found.node;
      }
    }

    const searchTree = (nodes: AreaProperty[]): AreaProperty | null => {
      for (const node of nodes) {
        if (isLinked(node.options)) return node;
        const found = node.children?.length ? searchTree(node.children) : null;
        if (found) return found;
      }
      return null;
    };

    return searchTree(this.areaPropertyByHierarchy);
  }

  /**
   * Pregunta antes de vincular una sección suelta a una opción de lista sin
   * sección todavía. Devuelve false si el usuario cancela.
   */
  private async confirmLinkAsNewOption(movedItem: AreaProperty, listProp: AreaProperty): Promise<boolean> {
    const modal = await this.modalController.create({
      component: DeleteModalComponent,
      componentProps: {
        modalTitle: 'Unir sección como opción de la lista',
        message: `¿Quieres unir esta sección a la lista "${listProp.label}"?\n\n`
          + `Se creará la opción "${movedItem.label}", y esta sección solo se mostrará cuando el usuario la elija.\n\n`
          + `¿Continuar?`,
        confirmLabel: 'Continuar',
        cancelColor: 'warn',
        confirmColor: 'primary',
      },
      cssClass: 'auto-height',
      backdropDismiss: false,
    });
    await modal.present();
    const { data: confirmed } = await modal.onDidDismiss();
    return !!confirmed;
  }

  /** Mueve `section` dentro de `listProp`, creando una opción nueva vinculada a ella. */
  private attachAsNewOption(section: AreaProperty, listProp: AreaProperty): void {
    const listKeyName = this.ensureListKeyName(listProp.id);
    const optionValue = section.label;

    section.parentId = listProp.id;
    section.visibilityCondition = {
      operator: 'AND',
      rules: [{ field: listKeyName, op: 'eq', value: optionValue }],
    };

    listProp.children ??= [];
    listProp.children.push(section);

    const newOption: AreaPropertyOption = { label: optionValue, value: optionValue, sectionAreaPropertyId: section.id };
    listProp.options = [...(listProp.options ?? []), newOption];

    if (this.editingPropertyId === listProp.id) {
      const formOptions = (this.editForm.get('options')?.value || []) as AreaPropertyOption[];
      this.editForm.patchValue({ options: [...formOptions, newOption] }, { emitEvent: false });
    }
  }

  private syncEditingParentControl(node: AreaProperty): void {
    if (!this.editingPropertyId || this.editingPropertyId !== node.id) return;
    this.editForm.patchValue(
      { parentId: node.parentId ?? null },
      { emitEvent: false }
    );
  }

  // ────────────────────────────────────────────────
  // Sincronizar árbol <-> lista plana
  // ────────────────────────────────────────────────

  private updateFlatPropertiesFromTree(): void {
    const flat = this.treeService.flattenTree(this.areaPropertyByHierarchy);
    this.properties.length = 0;
    this.properties.push(...flat);
    this.syncNodeCollapseState();
  }

  // ────────────────────────────────────────────────
  // Duplicar propiedad / sección
  // ────────────────────────────────────────────────

  duplicateProperty(prop: AreaProperty, event: MouseEvent): void {
    event.stopPropagation();
    const duplicate = this.treeService.clonePropertyTree(prop);
    const found = this.treeService.locateNodeInTree(prop.id, this.areaPropertyByHierarchy);
    if (found) {
      const idx = found.list.indexOf(found.node);
      found.list.splice(idx + 1, 0, duplicate);
    } else {
      this.areaPropertyByHierarchy.push(duplicate);
    }
    this.updateFlatPropertiesFromTree();
  }

  private syncNodeCollapseState(): void {
    const currentNodeIds = new Set<string>();
    const currentCollapsibleNodeIds = new Set<string>();

    const walk = (list: AreaProperty[]) => {
      for (const node of list) {
        if (node.id) {
          currentNodeIds.add(node.id);
        }

        if (this.isCollapsibleNode(node) && node.id) {
          currentCollapsibleNodeIds.add(node.id);
          if (!this.initializedNodeCollapseIds.has(node.id)) {
            if (this.getNodeContentCount(node) >= this.autoCollapseThreshold) {
              this.collapsedNodeIds.add(node.id);
            }
            this.initializedNodeCollapseIds.add(node.id);
          }
        }

        if (node.children?.length) {
          walk(node.children);
        }
      }
    };

    walk(this.areaPropertyByHierarchy);

    Array.from(this.collapsedNodeIds).forEach((nodeId: string) => {
      if (!currentCollapsibleNodeIds.has(nodeId)) {
        this.collapsedNodeIds.delete(nodeId);
      }
    });

    Array.from(this.initializedNodeCollapseIds).forEach((nodeId: string) => {
      if (!currentCollapsibleNodeIds.has(nodeId)) {
        this.initializedNodeCollapseIds.delete(nodeId);
      }
    });
  }

  private countDescendants(list: AreaProperty[]): number {
    let total = 0;

    for (const node of list) {
      total += 1;
      if (node.children?.length) {
        total += this.countDescendants(node.children);
      }
    }

    return total;
  }

  private isCollapsibleNode(node: AreaProperty): boolean {
    return !!node.id && !!node.children?.length;
  }

  private nodeContainsId(node: AreaProperty, targetId: string | null): boolean {
    if (!targetId) return false;
    if (node.id === targetId) return true;
    return (node.children ?? []).some((child: AreaProperty) => this.nodeContainsId(child, targetId));
  }

}
